import { createFileRoute } from '@tanstack/react-router'
import { Header } from '@/components/layout/Header'
import { Footer } from '@/components/layout/Footer'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Search, Mail, FileText, HelpCircle } from 'lucide-react'
import { useState } from 'react'

export const Route = createFileRoute('/docs/faq')({
  component: FAQPage
})

function FAQPage() {
  const [searchTerm, setSearchTerm] = useState('')

  const faqCategories = [
    {
      title: 'Getting Started',
      description: 'Basic questions about setting up and using our API',
      icon: <HelpCircle className="w-5 h-5" />,
      questions: [
        {
          question: 'How do I get started with the API?',
          answer: 'First, create an account and generate your API key from the dashboard. Then follow our quick start guide to make your first API call.'
        },
        {
          question: 'What authentication method do you use?',
          answer: 'We use API key authentication. Include your API key in the Authorization header as "Bearer YOUR_API_KEY".'
        },
        {
          question: 'Are there rate limits?',
          answer: 'Yes, rate limits vary by plan. Free accounts get 1,000 requests per month, Pro gets 10,000, and Enterprise has custom limits.'
        },
        {
          question: 'Do you have SDKs available?',
          answer: 'Yes, we provide official SDKs for Python, JavaScript, Go, and PHP. Community SDKs are available for other languages.'
        }
      ]
    },
    {
      title: 'Billing & Plans',
      description: 'Questions about pricing, billing, and plan features',
      icon: <FileText className="w-5 h-5" />,
      questions: [
        {
          question: 'How does billing work?',
          answer: 'Billing is based on API usage. You\'re charged monthly based on the number of requests made during that period.'
        },
        {
          question: 'Can I change my plan anytime?',
          answer: 'Yes, you can upgrade or downgrade your plan at any time. Changes take effect immediately.'
        },
        {
          question: 'What happens if I exceed my plan limits?',
          answer: 'Your API requests will be throttled once you reach your plan limit. Upgrade your plan to continue making requests.'
        },
        {
          question: 'Do you offer refunds?',
          answer: 'We offer a 30-day money-back guarantee for annual plans. Monthly plans are non-refundable.'
        }
      ]
    },
    {
      title: 'API Usage',
      description: 'Technical questions about API endpoints and usage',
      icon: <Search className="w-5 h-5" />,
      questions: [
        {
          question: 'What data formats do you support?',
          answer: 'Our API accepts and returns JSON data. We also support XML for certain endpoints.'
        },
        {
          question: 'How do I handle errors?',
          answer: 'Our API returns standard HTTP status codes. Check the response body for detailed error messages and error codes.'
        },
        {
          question: 'Is there a sandbox environment?',
          answer: 'Yes, we provide a sandbox environment for testing. Use the sandbox API key from your dashboard.'
        },
        {
          question: 'How do I monitor my API usage?',
          answer: 'You can monitor your usage in real-time from the dashboard. We also provide usage analytics and alerts.'
        }
      ]
    },
    {
      title: 'Security',
      description: 'Questions about API security and best practices',
      icon: <Mail className="w-5 h-5" />,
      questions: [
        {
          question: 'How secure is my data?',
          answer: 'We use industry-standard encryption and security practices. All data is encrypted in transit and at rest.'
        },
        {
          question: 'Can I rotate my API keys?',
          answer: 'Yes, you can generate new API keys and revoke old ones at any time from the dashboard.'
        },
        {
          question: 'What should I do if my API key is compromised?',
          answer: 'Immediately delete the compromised key from your dashboard and generate a new one. Update your applications with the new key.'
        },
        {
          question: 'Do you log API requests?',
          answer: 'We log basic request metadata for monitoring and debugging purposes. We do not log request bodies or sensitive data.'
        }
      ]
    },
    {
      title: 'Technical Support',
      description: 'Questions about getting help and support',
      icon: <HelpCircle className="w-5 h-5" />,
      questions: [
        {
          question: 'How can I get help?',
          answer: 'Free users get community support through our forum. Paid users get email support. Enterprise customers get priority support with dedicated channels.'
        },
        {
          question: 'What are your support hours?',
          answer: 'Email support is available 24/7 with response times based on your plan. Priority support gets 1-hour response during business hours.'
        },
        {
          question: 'Do you have a status page?',
          answer: 'Yes, you can check our system status and uptime at status.example.com. We also provide status updates via email and Slack.'
        },
        {
          question: 'How do I report a bug?',
          answer: 'You can report bugs through our support portal or email support@example.com. Include detailed steps to reproduce the issue.'
        }
      ]
    }
  ]

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => 
        q.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0)

  const totalQuestions = faqCategories.reduce((acc, cat) => acc + cat.questions.length, 0)
  const filteredQuestionCount = filteredCategories.reduce((acc, cat) => acc + cat.questions.length, 0)

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h1>
          <p className="text-xl text-gray-600 mb-8">
            Find answers to common questions about our API key management platform.
          </p>
          
          <div className="relative max-w-md mx-auto mb-6">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <Input
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          <div className="flex items-center justify-center space-x-4 text-sm text-gray-600">
            <Badge variant="secondary">
              {filteredQuestionCount} of {totalQuestions} questions
            </Badge>
            {searchTerm && (
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => setSearchTerm('')}
              >
                Clear search
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-8">
          {filteredCategories.map((category) => (
            <Card key={category.title}>
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <div className="p-2 bg-blue-100 rounded-lg text-blue-600">
                    {category.icon}
                  </div>
                  <div>
                    <CardTitle className="flex items-center space-x-2">
                      <span>{category.title}</span>
                      <Badge variant="outline">
                        {category.questions.length}
                      </Badge>
                    </CardTitle>
                    <CardDescription>{category.description}</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Accordion type="single" collapsible className="w-full">
                  {category.questions.map((faq, index) => (
                    <AccordionItem key={index} value={`item-${index}`}>
                      <AccordionTrigger className="text-left">
                        {faq.question}
                      </AccordionTrigger>
                      <AccordionContent className="text-gray-600">
                        {faq.answer}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </CardContent>
            </Card>
          ))}
        </div>

        {filteredCategories.length === 0 && searchTerm && (
          <Card>
            <CardContent className="text-center py-12">
              <Search className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No FAQs found
              </h3>
              <p className="text-gray-500 mb-6">
                No FAQs found matching "{searchTerm}". Try different keywords or browse all categories.
              </p>
              <Button onClick={() => setSearchTerm('')}>
                Clear search
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Contact Support Section */}
        <Card className="mt-12 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="p-8 text-center">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">
              Still need help?
            </h3>
            <p className="text-gray-600 mb-6">
              Can't find what you're looking for? Our support team is here to help.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild>
                <a href="mailto:support@example.com">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Support
                </a>
              </Button>
              <Button variant="outline" asChild>
                <a href="/docs">
                  <FileText className="w-4 h-4 mr-2" />
                  View Documentation
                </a>
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  )
}